// Step 2 · Címzettek: which countries, who in each, and how many that is after
// everything the platform leaves out on its own.
//
// Who in each is the whole country or the ticked segments, their union
// (UI-11, owner 2026-10-26): a country's subscription is its contact rows, so
// there is no list to pick. The whole country ("Összes") is the default; the
// exclusions, added on demand, and the cap go with either. The country is
// counted while its boxes are ticked (POST /audience-estimate).
//
// A country is added with its audience in one move (POST /countries), and
// from then on its audience is fixed: the API has no call that changes or
// removes a country of a group, so the step says so before the button, not
// after. The numbers are the estimate's (GET /estimate): the same conditions
// the snapshot selects with, counted per country.

import { useEffect, useMemo, useState } from "react";
import { RefreshCwIcon, XIcon } from "lucide-react";
import type { ColumnDef } from "@tanstack/react-table";
import { addGroupCountries, estimateAudience, fetchSegments } from "@/lib/api";
import type { AudienceEstimate, CountryEstimate, Segment, SendingIdentity } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardAction, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { NativeSelect } from "@/components/ui/native-select";
import { DataTable } from "@/components/emc/DataTable";
import { LimitBar } from "@/components/emc/charts/LimitBar";
import { Field } from "@/components/emc/Field";
import { StatusPill } from "@/components/emc/StatusPill";
import { Callout, ErrorState, LoadingState } from "@/components/emc/states";
import { count, shareOf } from "@/components/emc/format";
import { cn } from "@/lib/utils";
import type { Editor } from "@/pages/campaigns/editor/context";
import {
  CAP_DAYS_MAX,
  CAP_PRESETS,
  addExclusion,
  audienceInput,
  audienceProblem,
  audienceText,
  emptyAudience,
  exclusionChoices,
  leftOut,
  messageOf,
  removeExclusion,
  tickSegment,
  wholeCountry,
  windowText,
} from "@/pages/campaigns/model";
import type { AudienceDraft } from "@/pages/campaigns/model";
import { StepActions } from "@/pages/campaigns/parts";
import { Country } from "@/components/emc/Flag";
import { campaignChoice } from "@/pages/segments/klaviyo";

// How long the live count waits after the last tick before it asks.
const COUNT_AFTER_MS = 400;

// --- the step ------------------------------------------------------------------

export function AudienceStep({ editor }: { editor: Editor }) {
  const { summary, identities, estimate, editable } = editor;
  const added = useMemo(
    () => new Map(summary.countries.map((row) => [row.country, row] as const)),
    [summary.countries],
  );
  const [drafts, setDrafts] = useState<Record<string, AudienceDraft>>({});
  const [segments, setSegments] = useState<Record<string, Segment[] | string>>({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const chosen = Object.keys(drafts).filter((country) => !added.has(country));
  const problems = chosen
    .map((country) => audienceProblem(country, drafts[country] ?? emptyAudience()))
    .filter((problem): problem is string => problem !== null);

  // The estimate is the step's answer; ask for it once there is something to
  // count, and again after every addition.
  useEffect(() => {
    if (summary.countries.length > 0 && estimate.rows === null && !estimate.loading) {
      void estimate.load();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [summary.countries.length]);

  function pick(country: string, on: boolean) {
    setDrafts((current) => {
      const next = { ...current };
      if (on) next[country] = current[country] ?? emptyAudience();
      else delete next[country];
      return next;
    });
    if (!on) return;
    // The segments are fetched with the whole country chosen too: the
    // exclusions are segments, and they go with either choice.
    if (segments[country] === undefined) {
      fetchSegments(country)
        .then((rows) =>
          setSegments((current) => ({
            ...current,
            [country]: rows.filter((row) => row.state !== "archived"),
          })),
        )
        .catch((raised: unknown) =>
          setSegments((current) => ({ ...current, [country]: messageOf(raised) })),
        );
    }
  }

  function update(country: string, next: AudienceDraft) {
    setDrafts((current) => ({ ...current, [country]: next }));
  }

  async function addChosen(): Promise<boolean> {
    if (chosen.length === 0) return true;
    if (problems.length > 0) return false;
    setBusy(true);
    setError(null);
    try {
      await addGroupCountries(
        editor.groupId,
        chosen.map((country) => audienceInput(country, drafts[country] ?? emptyAudience())),
      );
      setDrafts({});
      await editor.reload();
      void estimate.load();
      return true;
    } catch (raised: unknown) {
      setError(messageOf(raised));
      return false;
    } finally {
      setBusy(false);
    }
  }

  async function next() {
    if (await addChosen()) editor.go("tartalom");
  }

  const columns: ColumnDef<SendingIdentity>[] = [
    {
      id: "pick",
      header: () => <span className="sr-only">Kiválasztva</span>,
      cell: ({ row }) => {
        const country = row.original.country;
        return (
          <Checkbox
            aria-label={`${country} kiválasztása`}
            checked={added.has(country) || country in drafts}
            disabled={!editable || added.has(country)}
            onChange={(event) => pick(country, event.target.checked)}
          />
        );
      },
    },
    {
      id: "country",
      header: "Ország",
      cell: ({ row }) => (
        <span className="flex flex-col">
          <Country code={row.original.country} />
          <span className="text-label text-muted-foreground">{row.original.label}</span>
        </span>
      ),
    },
    {
      // Rule 5: the sending account's, shown and never offered.
      id: "sender",
      header: "Feladó",
      cell: ({ row }) => (
        <span className="flex flex-col">
          <span>{row.original.sender_name}</span>
          <span className="data-mono text-label text-muted-foreground">{row.original.sender_email}</span>
        </span>
      ),
    },
    {
      id: "zone",
      header: "Időzóna · küldési ablak",
      cell: ({ row }) => (
        <span className="flex flex-col">
          <span className="data-mono">{row.original.timezone}</span>
          <span className="text-label text-muted-foreground">{windowText(row.original)}</span>
        </span>
      ),
    },
    {
      id: "audience",
      header: "Közönség",
      cell: ({ row }) => {
        const existing = added.get(row.original.country);
        if (existing !== undefined) {
          return (
            <span className="flex flex-col items-start gap-1">
              <StatusPill domain="campaign" state={existing.state} />
              <span className="text-muted-foreground">
                {audienceText(existing.audience, existing.frequency_cap_days)}
              </span>
            </span>
          );
        }
        return row.original.country in drafts ? (
          <span className="text-muted-foreground">beállítás a sor alatt</span>
        ) : (
          <span className="text-muted-foreground">—</span>
        );
      },
      meta: { wrap: true },
    },
  ];

  return (
    <div className="flex flex-col gap-6">
      <Card>
        <CardHeader>
          <CardTitle>Célországok és közönség</CardTitle>
          <CardDescription>
            Minden ország a saját közönségével, a saját feladójával és a saját nyelvén kap levelet.
            A feladó, a cégadatok és a leiratkozó szöveg a küldő fiókból jön, kampányból nem írható
            felül.
          </CardDescription>
        </CardHeader>
        <CardContent className="flex flex-col gap-4">
          {editor.identities.length === 0 && <LoadingState lines={3} label="Az országok betöltése…" />}
          {error !== null && <ErrorState message={error} />}
          <DataTable
            label="Célországok"
            variant="plain"
            className="-mx-5.5 border-t border-separator"
            columns={columns}
            data={identities}
            getRowId={(row) => row.country}
            rowClassName={(row) => (row.country in drafts ? "bg-primary-soft" : undefined)}
            renderExpanded={(row) =>
              row.country in drafts && !added.has(row.country) ? (
                <AudienceEditor
                  country={row.country}
                  draft={drafts[row.country] ?? emptyAudience()}
                  segments={segments[row.country]}
                  onChange={(next) => update(row.country, next)}
                />
              ) : null
            }
          />
          {editable && chosen.length > 0 && (
            <div className="flex flex-wrap items-center justify-between gap-3 rounded-card border border-line bg-surface-2 px-4 py-3">
              <p className="max-w-prose text-muted-foreground">
                Hozzáadás után az ország közönsége nem módosítható, és az ország nem vehető ki a
                kampányból. Nézd át a választást, mielőtt hozzáadod.
              </p>
              <div className="flex flex-col items-end gap-1">
                <Button
                  type="button"
                  disabled={busy || problems.length > 0}
                  onClick={() => void addChosen()}
                >
                  {busy ? "Hozzáadás…" : `Hozzáadás: ${chosen.join(", ")}`}
                </Button>
                {problems.length > 0 && (
                  <p className="text-right text-label text-muted-foreground">{problems[0]}</p>
                )}
              </div>
            </div>
          )}
          {!editable && (
            <Callout>
              Az országok a vázlat állapotban választhatók. A kampány most:{" "}
              <StatusPill domain="campaignGroup" state={summary.state} />
            </Callout>
          )}
        </CardContent>
      </Card>

      {summary.countries.length > 0 && <EstimateCard editor={editor} />}

      <ExclusionsExplained />

      <StepActions
        onBack={() => editor.go("alapok")}
        next={{
          label: chosen.length > 0 ? "Hozzáadás és tovább" : "Tovább a tartalomhoz",
          onClick: () => void next(),
          disabled: (summary.countries.length === 0 && chosen.length === 0) || problems.length > 0,
          reason:
            summary.countries.length === 0 && chosen.length === 0
              ? "Válassz legalább egy országot."
              : (problems[0] ?? null),
          busy,
        }}
      />
    </div>
  );
}

// --- one new country's audience ------------------------------------------------

function AudienceEditor({
  country,
  draft,
  segments,
  onChange,
}: {
  country: string;
  draft: AudienceDraft;
  segments: Segment[] | string | undefined;
  onChange: (next: AudienceDraft) => void;
}) {
  const segmentRows = Array.isArray(segments) ? segments : [];
  const [adding, setAdding] = useState(false);
  const nameOf = (id: number) => segmentRows.find((row) => row.id === id)?.name ?? `#${id}`;
  const offered = exclusionChoices(segmentRows, draft).filter((row) => campaignChoice(row).disabled === undefined);
  return (
    <div className="grid gap-5 rounded-card border border-line bg-surface p-4 lg:grid-cols-2">
      <fieldset className="m-0 flex min-w-0 flex-col gap-3 border-0 p-0">
        <legend className="mb-2 flex w-full items-center justify-between gap-3 font-medium">
          <span>
            Közönség · <span className="data-mono">{country}</span>
          </span>
          <Button
            type="button"
            size="sm"
            variant={draft.wholeCountry ? "secondary" : "outline"}
            onClick={() => onChange(wholeCountry(draft))}
          >
            Összes
          </Button>
        </legend>
        <ul className="m-0 flex max-h-64 list-none flex-col gap-1.5 overflow-y-auto p-0">
          <li>
            <label className="flex cursor-pointer items-center gap-2">
              <Checkbox
                checked={draft.wholeCountry}
                onChange={(event) =>
                  onChange(event.target.checked ? wholeCountry(draft) : { ...draft, wholeCountry: false })
                }
              />
              Az egész ország
              <span className="text-muted-foreground">— mindenki, akinek küldhetünk</span>
            </label>
          </li>
          {segments === undefined && (
            <li>
              <LoadingState lines={2} label="A szegmensek betöltése…" />
            </li>
          )}
          {typeof segments === "string" && (
            <li>
              <ErrorState message={segments} />
            </li>
          )}
          {segmentRows.map((row) => {
            // A Klaviyo engagement segment is marked, and offered once its
            // members are there (3.2).
            const choice = campaignChoice(row);
            const ticked = draft.segmentIds.includes(row.id);
            return (
              <li key={row.id}>
                <label className="flex cursor-pointer items-center gap-2" title={choice.disabled}>
                  <Checkbox
                    checked={ticked}
                    disabled={choice.disabled !== undefined && !ticked}
                    onChange={(event) => onChange(tickSegment(draft, row.id, event.target.checked))}
                  />
                  {choice.label}
                  {choice.disabled ? <span className="text-muted-foreground">({choice.disabled})</span> : null}
                </label>
              </li>
            );
          })}
        </ul>
        <LiveCount country={country} draft={draft} />
      </fieldset>

      <div className="flex min-w-0 flex-col gap-4">
        <div className="flex flex-col gap-1.5">
          <span className="font-medium">Kizárás</span>
          {draft.excludeIds.length > 0 && (
            <ul className="m-0 flex list-none flex-wrap gap-1.5 p-0">
              {draft.excludeIds.map((id) => (
                <li key={id}>
                  <span className="inline-flex items-center gap-1 rounded-control bg-fill py-0.5 pr-0.5 pl-2">
                    {nameOf(id)}
                    <Button
                      type="button"
                      size="icon-sm"
                      variant="ghost"
                      aria-label={`${nameOf(id)} kizárásának törlése`}
                      onClick={() => onChange(removeExclusion(draft, id))}
                    >
                      <XIcon />
                    </Button>
                  </span>
                </li>
              ))}
            </ul>
          )}
          {adding ? (
            <NativeSelect
              aria-label="Kizárandó szegmens"
              className="w-full"
              value=""
              onChange={(event) => {
                if (event.target.value !== "") onChange(addExclusion(draft, Number(event.target.value)));
                setAdding(false);
              }}
              onBlur={() => setAdding(false)}
              autoFocus
            >
              <option value="">— válassz szegmenst —</option>
              {offered.map((row) => (
                <option key={row.id} value={row.id}>
                  {campaignChoice(row).label}
                </option>
              ))}
            </NativeSelect>
          ) : (
            <Button
              type="button"
              size="sm"
              variant="ghost"
              className="self-start"
              disabled={offered.length === 0}
              onClick={() => setAdding(true)}
            >
              + kizárás hozzáadása
            </Button>
          )}
        </div>
        <Field
          label="Pihentetés (nap)"
          htmlFor={`cap-${country}`}
          hint="Aki az elmúlt ennyi napban kapott tőlünk levelet, most kimarad. 0: csak az ország általános gyakorisági korlátja érvényes."
        >
          <div className="flex flex-wrap items-center gap-2">
            <Input
              id={`cap-${country}`}
              className="w-24"
              type="number"
              min={0}
              max={CAP_DAYS_MAX}
              step={1}
              value={draft.capDays}
              onChange={(event) => onChange({ ...draft, capDays: event.target.value })}
            />
            {CAP_PRESETS.map((days) => (
              <Button
                key={days}
                type="button"
                size="sm"
                variant={Number(draft.capDays || "0") === days ? "secondary" : "outline"}
                onClick={() => onChange({ ...draft, capDays: String(days) })}
              >
                {days}
              </Button>
            ))}
            <span className="text-muted-foreground">nap</span>
          </div>
        </Field>
      </div>
    </div>
  );
}

/** The country's number while its boxes are ticked: the estimate's own
 * counting, asked a moment after the last change. */
function LiveCount({ country, draft }: { country: string; draft: AudienceDraft }) {
  const problem = audienceProblem(country, draft);
  const key = JSON.stringify(audienceInput(country, draft));
  const [found, setFound] = useState<{ key: string; value: AudienceEstimate | string } | null>(null);

  useEffect(() => {
    if (problem !== null) return;
    let live = true;
    const timer = window.setTimeout(() => {
      estimateAudience(JSON.parse(key))
        .then((value) => live && setFound({ key, value }))
        .catch((raised: unknown) => live && setFound({ key, value: messageOf(raised) }));
    }, COUNT_AFTER_MS);
    return () => {
      live = false;
      window.clearTimeout(timer);
    };
  }, [key, problem]);

  if (problem !== null) return <p className="text-muted-foreground">{problem}</p>;
  if (found === null || found.key !== key) return <p className="text-muted-foreground">Számolás…</p>;
  if (typeof found.value === "string") return <ErrorState message={found.value} />;
  const value = found.value;
  return (
    <p className="text-muted-foreground">
      Becslés: <span className="font-semibold text-ink">{count(value.recipients)} címzett</span> · {leftOut(value).text}
    </p>
  );
}

// --- the numbers -----------------------------------------------------------------------

function EstimateCard({ editor }: { editor: Editor }) {
  const { estimate } = editor;
  const rows = estimate.rows ?? [];
  const total = rows.reduce((sum, row) => sum + row.recipients, 0);

  const columns: ColumnDef<CountryEstimate>[] = [
    {
      id: "country",
      header: "Ország",
      cell: ({ row }) => <Country code={row.original.country} />,
    },
    {
      id: "recipients",
      header: "Címzett",
      cell: ({ row }) => (
        <span className="flex flex-col items-end">
          <span className="font-semibold">{count(row.original.recipients)}</span>
          <span className="text-label text-muted-foreground">
            {shareOf(
              row.original.recipients,
              row.original.candidates,
              `a(z) ${count(row.original.candidates)} feliratkozott`,
            ) ?? "nincs feliratkozott"}
          </span>
        </span>
      ),
      meta: { align: "right" },
    },
    {
      // The buckets that are not recipients, in one cell: the step
      // column is narrower than the page, and a table that scrolls sideways
      // hides the warmup bar that matters most.
      id: "left-out",
      header: "Kimarad",
      cell: ({ row }) => (
        <span className="flex flex-col gap-0.5 text-muted-foreground">
          <span>
            kizárva <span className="text-ink">{count(row.original.suppressed)}</span> · pihentetve{" "}
            <span className="text-ink">{count(row.original.frequency_capped)}</span> · kizáró szegmens{" "}
            <span className="text-ink">{count(row.original.excluded_by_segment)}</span>
          </span>
          <span>nincs a közönségben: {count(row.original.not_in_audience)}</span>
          {(row.original.absent ?? 0) > 0 && (
            <span>
              nincs jelen a Klaviyóban: <span className="text-ink">{count(row.original.absent ?? 0)}</span>
            </span>
          )}
          {row.original.handover_wave != null && (
            <span>
              a Klaviyóé (N = {row.original.handover_wave}):{" "}
              <span className="text-ink">{count(row.original.handover ?? 0)}</span>
            </span>
          )}
          {row.original.handover_parallel === true && <span>a Klaviyo is küld nekik — nincs átadás-szűrés</span>}
        </span>
      ),
      meta: { wrap: true },
    },
    {
      id: "room",
      header: "Mai warmup-keret",
      cell: ({ row }) => (
        <LimitBar
          className="min-w-44"
          used={row.original.recipients}
          limit={row.original.pool_room_today}
          label={`${row.original.pool_code} pool`}
          tone={row.original.fits_today ? "running" : "warn"}
        />
      ),
    },
  ];

  return (
    <Card>
      <CardHeader>
        <CardTitle>Becsült címzettek</CardTitle>
        <CardDescription>
          Ugyanazokkal a feltételekkel számolva, amikkel a kiküldés összeállítja a listát. Az
          oszlopok országonként kiadják az ország összes feliratkozottját.
          {estimate.at !== null && (
            <>
              {" "}
              Számolva: <span className="data-mono">{estimate.at.toLocaleTimeString("hu-HU")}</span>.
            </>
          )}
        </CardDescription>
        <CardAction>
          <Button
            variant="outline"
            size="sm"
            type="button"
            disabled={estimate.loading}
            onClick={() => void estimate.load()}
          >
            <RefreshCwIcon />
            {estimate.loading ? "Számolás…" : "Újraszámolás"}
          </Button>
        </CardAction>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        {estimate.error !== null && <ErrorState message={estimate.error} onRetry={() => void estimate.load()} />}
        <DataTable
          label="Becsült címzettek országonként"
          variant="plain"
          className="-mx-5.5 border-t border-separator"
          columns={columns}
          data={rows}
          loading={estimate.loading && estimate.rows === null}
          getRowId={(row) => row.country}
          empty={<p className="px-4 py-6 text-center text-muted-foreground">Még nincs becslés.</p>}
          footer={
            rows.length > 0 ? (
              <div className="flex flex-wrap justify-between gap-3">
                <span className="text-muted-foreground">Összesen, {rows.length} ország</span>
                <span className="font-semibold">{count(total)} címzett</span>
              </div>
            ) : undefined
          }
        />
        {rows
          .filter((row) => row.warning !== "")
          .map((row) => (
            <Callout key={row.country} tone="warn">
              <span className="data-mono">{row.country}</span>: {row.warning} A keretet a kód
              tartja; ami ma nem fér bele, a következő napokon megy ki.
            </Callout>
          ))}
        {rows
          .filter((row) => (row.handover_message ?? "") !== "")
          .map((row) => (
            <Callout key={`handover-${row.country}`} tone="warn">
              {row.handover_message}
            </Callout>
          ))}
      </CardContent>
    </Card>
  );
}

function ExclusionsExplained() {
  return (
    <Card className={cn("gap-3 bg-surface-2 shadow-none")}>
      <CardHeader>
        <CardTitle>Akiket a rendszer magától kihagy</CardTitle>
      </CardHeader>
      <CardContent>
        <ul className="m-0 flex list-disc flex-col gap-1.5 pl-5 text-muted-foreground">
          <li>
            <span className="text-ink">Kizárva:</span> aki ebben az országban leiratkozott vagy a
            Klaviyo-fiókban kézzel kizárták, és aki bárhol spam-panaszt tett, végleg visszapattant,
            érvénytelen a címe, vagy a szolgáltatója letiltotta. A panasz és a visszapattanás minden
            országban kizár, a leiratkozás csak abban az országban, ahol történt.
          </li>
          <li>
            <span className="text-ink">Nincs jelen:</span> akinek a profilját azóta törölték az
            ország Klaviyo-fiókjából.
          </li>
          <li>
            <span className="text-ink">Pihentetve:</span> aki elérte az ország gyakorisági korlátját,
            vagy — ha megadtál ilyet — az elmúlt napokban már kapott levelet. A korlát a más
            kampányokban már tervezett, még el nem küldött leveleket is számolja: aki két, egymást
            fedő kampányban is benne van, nem kap több levelet, mint amennyit a korlát enged.
          </li>
          <li>
            <span className="text-ink">Kizáró szegmens:</span> aki a „Kizárás” alatt megadott
            szegmensben van.
          </li>
          <li>
            <span className="text-ink">Nincs feliratkozva:</span> aki nem járult hozzá, nem is
            számít bele.
          </li>
          <li>
            <span className="text-ink">A Klaviyóé (migráció):</span> amíg egy ország Klaviyo-fiókja
            még küld, csak azt a kontaktot érjük el, akinek a hullámát a Klaviyo már megkapta, és a
            hulláma legfeljebb N. A többieknek a Klaviyo küld — tőlünk nem kapnak levelet, hogy senki
            ne kapjon kettőt. N-et a Warmup oldal Klaviyo-átadás része állítja.
          </li>
        </ul>
        <p className="mt-3 text-muted-foreground">
          A kizárásokat a kiküldés még kétszer újraellenőrzi: a csomag kiadásakor és közvetlenül a
          levél elküldése előtt — aki közben panaszt tesz, a hátralévő adagokból kimarad.
        </p>
      </CardContent>
    </Card>
  );
}
